import { useEffect, useState, type FormEvent, type SVGProps } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AlertBanner,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Heading,
  Input,
  Label,
  SelectField,
  Textarea,
} from "@lendcord/concord";
import { isQueryApiError } from "@/app/store/baseApi";
import { useCreateLoanSchemeMutation, useUpdateLoanSchemeMutation } from "../api/loanSchemes.api";
import type {
  Charge,
  ChargeType,
  EmiType,
  LoanScheme,
  LoanSchemeRequest,
  PenaltyCalculationType,
  PenaltyRule,
  PenaltyType,
} from "../setup.types";

interface LoanSchemeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Undefined = create mode; a LoanScheme = edit mode, pre-filled from this row.
  scheme?: LoanScheme;
}

// "" only ever appears pre-seed; "NONE" is the explicit "not charged" choice
// (a plain unselected Select can't be navigated back to once a value is
// picked, so it needs its own option in the list).
type ChargeFormState = { type: ChargeType | "NONE" | ""; value: string };
const EMPTY_CHARGE: ChargeFormState = { type: "NONE", value: "" };

interface PenaltyRuleFormState {
  fromDays: string;
  toDays: string;
  calculationType: PenaltyCalculationType | "";
  parameter: string;
}
const EMPTY_RULE: PenaltyRuleFormState = { fromDays: "", toDays: "", calculationType: "", parameter: "" };

interface FormState {
  schemeCode: string;
  name: string;
  minLoanAmount: string;
  maxLoanAmount: string;
  annualInterestRate: string;
  preClosureCharge: ChargeFormState;
  processingFee: ChargeFormState;
  collectionCharge: ChargeFormState;
  stampDutyCharge: ChargeFormState;
  otherEmiCharge: ChargeFormState;
  emiType: EmiType | "";
  collectorCommissionRate: string;
  remarks: string;
  firstEmiDeductOnDisbursement: boolean;
  isActive: boolean;
  penaltyType: PenaltyType | "";
  penaltyRules: PenaltyRuleFormState[];
}

const EMPTY_FORM: FormState = {
  schemeCode: "",
  name: "",
  minLoanAmount: "",
  maxLoanAmount: "",
  annualInterestRate: "",
  preClosureCharge: EMPTY_CHARGE,
  processingFee: EMPTY_CHARGE,
  collectionCharge: EMPTY_CHARGE,
  stampDutyCharge: EMPTY_CHARGE,
  otherEmiCharge: EMPTY_CHARGE,
  emiType: "",
  collectorCommissionRate: "",
  remarks: "",
  firstEmiDeductOnDisbursement: false,
  isActive: true,
  penaltyType: "",
  penaltyRules: [],
};

function chargeFromResponse(type: ChargeType | null, value: number | null): ChargeFormState {
  if (!type) return EMPTY_CHARGE;
  return { type, value: value != null ? String(value) : "" };
}

function toFormState(scheme: LoanScheme | undefined): FormState {
  if (!scheme) return EMPTY_FORM;
  return {
    schemeCode: scheme.schemeCode,
    name: scheme.name,
    minLoanAmount: scheme.minLoanAmount != null ? String(scheme.minLoanAmount) : "",
    maxLoanAmount: String(scheme.maxLoanAmount),
    annualInterestRate: String(scheme.annualInterestRate),
    preClosureCharge: chargeFromResponse(scheme.preClosureChargeType, scheme.preClosureChargeValue),
    processingFee: chargeFromResponse(scheme.processingFeeType, scheme.processingFeeValue),
    collectionCharge: chargeFromResponse(scheme.collectionChargeType, scheme.collectionChargeValue),
    stampDutyCharge: chargeFromResponse(scheme.stampDutyChargeType, scheme.stampDutyChargeValue),
    otherEmiCharge: chargeFromResponse(scheme.otherEmiChargeType, scheme.otherEmiChargeValue),
    emiType: scheme.emiType,
    collectorCommissionRate: scheme.collectorCommissionRate != null ? String(scheme.collectorCommissionRate) : "",
    remarks: scheme.remarks ?? "",
    firstEmiDeductOnDisbursement: scheme.firstEmiDeductOnDisbursement,
    isActive: scheme.isActive,
    penaltyType: scheme.penaltyType,
    penaltyRules: scheme.penaltyRules.map((rule) => ({
      fromDays: String(rule.fromDays),
      toDays: String(rule.toDays),
      calculationType: rule.calculationType,
      parameter: String(rule.parameter),
    })),
  };
}

function chargeToRequest(charge: ChargeFormState): Charge | undefined {
  if (!charge.type || charge.type === "NONE" || !charge.value) return undefined;
  return { type: charge.type, value: Number(charge.value) };
}

function TrashIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6" />
    </svg>
  );
}

export function LoanSchemeFormDialog({ open, onOpenChange, scheme }: LoanSchemeFormDialogProps) {
  const intl = useIntl();
  const isEditMode = scheme !== undefined;
  const [createLoanScheme, { isLoading: isCreating }] = useCreateLoanSchemeMutation();
  const [updateLoanScheme, { isLoading: isUpdating }] = useUpdateLoanSchemeMutation();
  const isSubmitting = isCreating || isUpdating;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [formError, setFormError] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setForm(toFormState(scheme));
      setErrors({});
      setFormError(undefined);
    }
  }, [open, scheme]);

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function updateRule(index: number, patch: Partial<PenaltyRuleFormState>) {
    setForm((current) => {
      const nextRules = [...current.penaltyRules];
      nextRules[index] = { ...nextRules[index], ...patch };
      return { ...current, penaltyRules: nextRules };
    });
  }

  function addRule() {
    setForm((current) => ({ ...current, penaltyRules: [...current.penaltyRules, EMPTY_RULE] }));
  }

  function removeRule(index: number) {
    setForm((current) => ({ ...current, penaltyRules: current.penaltyRules.filter((_, i) => i !== index) }));
  }

  function validate(): boolean {
    const nextErrors: Partial<Record<string, string>> = {};
    if (!form.schemeCode.trim()) nextErrors.schemeCode = intl.formatMessage({ id: "lender.loanSchemes.schemeCodeError" });
    if (!form.name.trim()) nextErrors.name = intl.formatMessage({ id: "lender.loanSchemes.nameError" });
    if (!form.maxLoanAmount || Number(form.maxLoanAmount) <= 0) {
      nextErrors.maxLoanAmount = intl.formatMessage({ id: "lender.loanSchemes.maxLoanAmountError" });
    }
    if (
      form.minLoanAmount &&
      form.maxLoanAmount &&
      Number(form.minLoanAmount) > Number(form.maxLoanAmount)
    ) {
      nextErrors.minLoanAmount = intl.formatMessage({ id: "lender.loanSchemes.minMaxError" });
    }
    if (!form.annualInterestRate || Number(form.annualInterestRate) < 0) {
      nextErrors.annualInterestRate = intl.formatMessage({ id: "lender.loanSchemes.annualInterestRateError" });
    }
    if (!form.emiType) nextErrors.emiType = intl.formatMessage({ id: "lender.loanSchemes.emiTypeError" });
    if (!form.penaltyType) nextErrors.penaltyType = intl.formatMessage({ id: "lender.loanSchemes.penaltyTypeError" });

    (["preClosureCharge", "processingFee", "collectionCharge", "stampDutyCharge", "otherEmiCharge"] as const).forEach(
      (key) => {
        const charge = form[key];
        if (charge.type && charge.type !== "NONE" && !charge.value) {
          nextErrors[key] = intl.formatMessage({ id: "lender.loanSchemes.chargeValueError" });
        }
      },
    );

    form.penaltyRules.forEach((rule, index) => {
      if (!rule.fromDays || Number(rule.fromDays) < 0) nextErrors[`rule${index}FromDays`] = intl.formatMessage({ id: "lender.loanSchemes.ruleFromDaysError" });
      if (!rule.toDays || Number(rule.toDays) <= 0) nextErrors[`rule${index}ToDays`] = intl.formatMessage({ id: "lender.loanSchemes.ruleToDaysError" });
      if (!rule.calculationType) nextErrors[`rule${index}CalculationType`] = intl.formatMessage({ id: "lender.loanSchemes.ruleCalculationTypeError" });
      if (!rule.parameter || Number(rule.parameter) < 0) nextErrors[`rule${index}Parameter`] = intl.formatMessage({ id: "lender.loanSchemes.ruleParameterError" });
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(undefined);
    if (!validate()) return;

    const body: LoanSchemeRequest = {
      schemeCode: form.schemeCode.trim(),
      name: form.name.trim(),
      maxLoanAmount: Number(form.maxLoanAmount),
      minLoanAmount: form.minLoanAmount ? Number(form.minLoanAmount) : undefined,
      annualInterestRate: Number(form.annualInterestRate),
      preClosureCharge: chargeToRequest(form.preClosureCharge),
      processingFee: chargeToRequest(form.processingFee),
      collectionCharge: chargeToRequest(form.collectionCharge),
      stampDutyCharge: chargeToRequest(form.stampDutyCharge),
      otherEmiCharge: chargeToRequest(form.otherEmiCharge),
      emiType: form.emiType as EmiType,
      collectorCommissionRate: form.collectorCommissionRate ? Number(form.collectorCommissionRate) : undefined,
      remarks: form.remarks.trim() || undefined,
      firstEmiDeductOnDisbursement: form.firstEmiDeductOnDisbursement,
      isActive: form.isActive,
      penaltyType: form.penaltyType as PenaltyType,
      penaltyRules: form.penaltyRules.map(
        (rule): PenaltyRule => ({
          fromDays: Number(rule.fromDays),
          toDays: Number(rule.toDays),
          calculationType: rule.calculationType as PenaltyCalculationType,
          parameter: Number(rule.parameter),
        }),
      ),
    };

    try {
      if (isEditMode) {
        await updateLoanScheme({ id: scheme.id, body }).unwrap();
      } else {
        await createLoanScheme(body).unwrap();
      }
      onOpenChange(false);
    } catch (error) {
      setFormError(
        isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.loanSchemes.saveError" }),
      );
    }
  }

  const chargeOptions = [
    { value: "NONE", label: intl.formatMessage({ id: "lender.loanSchemes.chargeTypeNotCharged" }) },
    { value: "FLAT", label: intl.formatMessage({ id: "lender.loanSchemes.chargeTypeFlat" }) },
    { value: "PERCENTAGE", label: intl.formatMessage({ id: "lender.loanSchemes.chargeTypePercentage" }) },
  ];

  function renderChargeRow(key: keyof Pick<FormState, "preClosureCharge" | "processingFee" | "collectionCharge" | "stampDutyCharge" | "otherEmiCharge">, labelId: string) {
    const charge = form[key];
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          label={intl.formatMessage({ id: labelId })}
          value={charge.type || "NONE"}
          onValueChange={(value) => updateField(key, { ...charge, type: value as ChargeFormState["type"] })}
          options={chargeOptions}
        />
        <FormField label={intl.formatMessage({ id: "lender.loanSchemes.chargeValueLabel" })} error={errors[key]}>
          <Input
            type="number"
            step="any"
            value={charge.value}
            onChange={(event) => updateField(key, { ...charge, value: event.target.value })}
            disabled={!charge.type || charge.type === "NONE"}
          />
        </FormField>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage id={isEditMode ? "lender.loanSchemes.editDialogTitle" : "lender.loanSchemes.addDialogTitle"} />
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
          {formError && <AlertBanner status="error">{formError}</AlertBanner>}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label={intl.formatMessage({ id: "lender.loanSchemes.schemeCodeLabel" })} error={errors.schemeCode} required>
              <Input value={form.schemeCode} onChange={(event) => updateField("schemeCode", event.target.value)} placeholder="e.g. BL001" />
            </FormField>
            <FormField label={intl.formatMessage({ id: "lender.loanSchemes.nameLabel" })} error={errors.name} required>
              <Input value={form.name} onChange={(event) => updateField("name", event.target.value)} placeholder="e.g. Standard Business Loan" />
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label={intl.formatMessage({ id: "lender.loanSchemes.minLoanAmountLabel" })} error={errors.minLoanAmount}>
              <Input type="number" step="any" value={form.minLoanAmount} onChange={(event) => updateField("minLoanAmount", event.target.value)} />
            </FormField>
            <FormField label={intl.formatMessage({ id: "lender.loanSchemes.maxLoanAmountLabel" })} error={errors.maxLoanAmount} required>
              <Input type="number" step="any" value={form.maxLoanAmount} onChange={(event) => updateField("maxLoanAmount", event.target.value)} />
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              label={intl.formatMessage({ id: "lender.loanSchemes.annualInterestRateLabel" })}
              error={errors.annualInterestRate}
              required
            >
              <Input type="number" step="any" value={form.annualInterestRate} onChange={(event) => updateField("annualInterestRate", event.target.value)} />
            </FormField>
            <SelectField
              label={intl.formatMessage({ id: "lender.loanSchemes.emiTypeLabel" })}
              placeholder={intl.formatMessage({ id: "lender.loanSchemes.emiTypePlaceholder" })}
              value={form.emiType}
              onValueChange={(value) => updateField("emiType", value as EmiType)}
              error={errors.emiType}
              required
              options={[
                { value: "FLAT", label: intl.formatMessage({ id: "lender.loanSchemes.emiTypeFlat" }) },
                { value: "REDUCING_BALANCE", label: intl.formatMessage({ id: "lender.loanSchemes.emiTypeReducingBalance" }) },
              ]}
            />
          </div>

          <Heading level={4} className="text-accent-600">
            <FormattedMessage id="lender.loanSchemes.chargesHeading" />
          </Heading>
          {renderChargeRow("preClosureCharge", "lender.loanSchemes.preClosureChargeLabel")}
          {renderChargeRow("processingFee", "lender.loanSchemes.processingFeeLabel")}
          {renderChargeRow("collectionCharge", "lender.loanSchemes.collectionChargeLabel")}
          {renderChargeRow("stampDutyCharge", "lender.loanSchemes.stampDutyChargeLabel")}
          {renderChargeRow("otherEmiCharge", "lender.loanSchemes.otherEmiChargeLabel")}

          <FormField label={intl.formatMessage({ id: "lender.loanSchemes.collectorCommissionRateLabel" })}>
            <Input
              type="number"
              step="any"
              value={form.collectorCommissionRate}
              onChange={(event) => updateField("collectorCommissionRate", event.target.value)}
            />
          </FormField>

          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="firstEmiDeductOnDisbursement"
                checked={form.firstEmiDeductOnDisbursement}
                onCheckedChange={(checked) => updateField("firstEmiDeductOnDisbursement", checked === true)}
              />
              <Label htmlFor="firstEmiDeductOnDisbursement">
                <FormattedMessage id="lender.loanSchemes.firstEmiDeductLabel" />
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="isActive"
                checked={form.isActive}
                onCheckedChange={(checked) => updateField("isActive", checked === true)}
              />
              <Label htmlFor="isActive">
                <FormattedMessage id="lender.loanSchemes.activeLabel" />
              </Label>
            </div>
          </div>

          <Heading level={4} className="text-accent-600">
            <FormattedMessage id="lender.loanSchemes.penaltyHeading" />
          </Heading>

          <SelectField
            label={intl.formatMessage({ id: "lender.loanSchemes.penaltyTypeLabel" })}
            placeholder={intl.formatMessage({ id: "lender.loanSchemes.penaltyTypePlaceholder" })}
            value={form.penaltyType}
            onValueChange={(value) => updateField("penaltyType", value as PenaltyType)}
            error={errors.penaltyType}
            required
            options={[
              { value: "FIXED", label: intl.formatMessage({ id: "lender.loanSchemes.penaltyTypeFixed" }) },
              { value: "INCREASING", label: intl.formatMessage({ id: "lender.loanSchemes.penaltyTypeIncreasing" }) },
            ]}
          />

          <div className="flex flex-col gap-4">
            {form.penaltyRules.map((rule, index) => (
              <div key={index} className="rounded-md border border-border p-4">
                <div className="mb-3 flex items-center justify-between">
                  <Heading level={4}>
                    <FormattedMessage id="lender.loanSchemes.ruleHeading" values={{ position: index + 1 }} />
                  </Heading>
                  <button
                    type="button"
                    aria-label={intl.formatMessage({ id: "lender.loanSchemes.removeRuleLabel" })}
                    onClick={() => removeRule(index)}
                    className="text-text-muted hover:text-error"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField label={intl.formatMessage({ id: "lender.loanSchemes.fromDaysLabel" })} error={errors[`rule${index}FromDays`]} required>
                    <Input type="number" value={rule.fromDays} onChange={(event) => updateRule(index, { fromDays: event.target.value })} />
                  </FormField>
                  <FormField label={intl.formatMessage({ id: "lender.loanSchemes.toDaysLabel" })} error={errors[`rule${index}ToDays`]} required>
                    <Input type="number" value={rule.toDays} onChange={(event) => updateRule(index, { toDays: event.target.value })} />
                  </FormField>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <SelectField
                    label={intl.formatMessage({ id: "lender.loanSchemes.calculationTypeLabel" })}
                    placeholder={intl.formatMessage({ id: "lender.loanSchemes.calculationTypePlaceholder" })}
                    value={rule.calculationType}
                    onValueChange={(value) => updateRule(index, { calculationType: value as PenaltyCalculationType })}
                    error={errors[`rule${index}CalculationType`]}
                    required
                    options={[
                      { value: "FLAT_AMOUNT", label: intl.formatMessage({ id: "lender.loanSchemes.calculationTypeFlatAmount" }) },
                      { value: "PERCENT_OF_OVERDUE_EMI", label: intl.formatMessage({ id: "lender.loanSchemes.calculationTypePercentOfOverdueEmi" }) },
                    ]}
                  />
                  <FormField label={intl.formatMessage({ id: "lender.loanSchemes.parameterLabel" })} error={errors[`rule${index}Parameter`]} required>
                    <Input type="number" step="any" value={rule.parameter} onChange={(event) => updateRule(index, { parameter: event.target.value })} />
                  </FormField>
                </div>
              </div>
            ))}
            <Button type="button" variant="secondary" density="compact" onClick={addRule} className="self-start">
              <FormattedMessage id="lender.loanSchemes.addRuleButton" />
            </Button>
          </div>

          <FormField label={intl.formatMessage({ id: "lender.loanSchemes.remarksLabel" })}>
            <Textarea value={form.remarks} onChange={(event) => updateField("remarks", event.target.value)} rows={2} />
          </FormField>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              <FormattedMessage id="lender.shared.cancelButton" />
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <FormattedMessage id="lender.loanSchemes.savingButton" />
              ) : (
                <FormattedMessage id={isEditMode ? "lender.loanSchemes.saveButton" : "lender.loanSchemes.addButton"} />
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
